/**
 * Máquina de estados del Asistente (bot de ayuda). Pura y sin red.
 * Regla de diseño: no infiere. Cada paso es una pregunta con opciones o texto,
 * y antes de enviar se confirma con un resumen («Pediré… ¿Es correcto?»).
 * Spec: docs/superpowers/specs/2026-10-05-bot-ayuda-feedback-design.md §3.1 y §4.
 */

export type Rating = 'util' | 'mejorable' | 'error' | 'duda';
export type Phase = 'asking' | 'clarifying' | 'confirming' | 'registered' | 'dismissed';
export interface Option { id: string; label: string; }
export interface Question { id: string; text: string; options: Option[]; multi?: boolean; freeText?: boolean; }
export interface Answer { questionId: string; pregunta: string; respuesta: string; optionIds: string[]; text?: string; }
export interface MachineState { phase: Phase; rating?: Rating; answers: Answer[]; question?: Question; }
export type Action =
  | { type: 'RATE'; rating: Rating }
  | { type: 'ANSWER'; optionIds: string[]; text?: string }
  | { type: 'CONFIRM' }
  | { type: 'CORRECT' }
  | { type: 'DISMISS' }
  | { type: 'RESET' };

export const INITIAL: MachineState = { phase: 'asking', answers: [] };

export const RATING_OPTIONS: { id: Rating; label: string }[] = [
  { id: 'util', label: 'Útil' },
  { id: 'mejorable', label: 'Mejorable' },
  { id: 'error', label: 'Tiene un error' },
  { id: 'duda', label: 'Tengo una duda' },
];

const Q: Record<string, Question> = {
  falta: { id: 'falta', text: '¿Qué le falta a la respuesta?', options: [
    { id: 'mas_corta', label: 'Más breve' }, { id: 'mas_desarrollo', label: 'Más desarrollo' },
    { id: 'falta_algo', label: 'Falta un tema' }] },
  conservar: { id: 'conservar', text: '¿Qué parte quieres conservar?', multi: true, options: [
    { id: 'conclusion', label: 'La conclusión' }, { id: 'norma', label: 'La norma' },
    { id: 'procedimiento', label: 'El procedimiento' }] },
  desarrollar: { id: 'desarrollar', text: '¿Qué quieres que se desarrolle?', options: [
    { id: 'fundamento', label: 'El fundamento' }, { id: 'aplicacion', label: 'La aplicación al caso' },
    { id: 'ejemplo', label: 'Un ejemplo' }] },
  tema: { id: 'tema', text: '¿Qué tema falta?', options: [], freeText: true },
  parte: { id: 'parte', text: '¿En qué parte está el error?', options: [
    { id: 'conclusion', label: 'La conclusión' }, { id: 'fundamento', label: 'El fundamento' },
    { id: 'procedimiento', label: 'El procedimiento' }, { id: 'otra', label: 'Otra parte' }] },
  // Ejes del verificador-fuentes (skill investigacion-tributaria-chile): existencia/fidelidad, vigencia, superación + dato.
  que_mal: { id: 'que_mal', text: '¿Qué está mal?', freeText: true, options: [
    { id: 'no_dice', label: 'La norma no existe o no dice eso' }, { id: 'vigencia', label: 'No es la versión vigente' },
    { id: 'superada', label: 'Un criterio posterior lo cambió' }, { id: 'dato', label: 'Un dato o plazo es incorrecto' }] },
  duda: { id: 'duda', text: '¿Qué no quedó claro?', options: [], freeText: true },
};

function optionId(s: MachineState, qid: string): string | undefined {
  return s.answers.find(a => a.questionId === qid)?.optionIds[0];
}

/** Siguiente pregunta según calificación y respuestas; undefined = toca confirmar. */
function nextQuestion(s: MachineState): Question | undefined {
  const n = s.answers.length;
  if (s.rating === 'mejorable') {
    if (n === 0) return Q.falta;
    if (n === 1) {
      const f = optionId(s, 'falta');
      return f === 'mas_corta' ? Q.conservar : f === 'mas_desarrollo' ? Q.desarrollar : Q.tema;
    }
    return undefined;
  }
  if (s.rating === 'error') return n === 0 ? Q.parte : n === 1 ? Q.que_mal : undefined;
  if (s.rating === 'duda') return n === 0 ? Q.duda : undefined;
  return undefined;
}

function isValid(q: Question, optionIds: string[], text?: string): boolean {
  const known = optionIds.every(id => q.options.some(o => o.id === id));
  if (!known) return false;
  const hasText = !!text && text.trim().length > 0;
  if (q.options.length === 0) return hasText;
  if (optionIds.length === 0) return false;
  return q.multi ? true : optionIds.length === 1;
}

function advance(s: MachineState): MachineState {
  const q = nextQuestion(s);
  return q ? { ...s, phase: 'clarifying', question: q } : { ...s, phase: 'confirming', question: undefined };
}

export function feedbackReducer(s: MachineState, a: Action): MachineState {
  switch (a.type) {
    case 'RESET': return INITIAL;
    case 'DISMISS': return { ...s, phase: 'dismissed', question: undefined };
    case 'RATE':
      if (s.phase !== 'asking') return s;
      if (a.rating === 'util') return { phase: 'registered', rating: 'util', answers: [] };
      return advance({ phase: 'clarifying', rating: a.rating, answers: [] });
    case 'ANSWER': {
      const q = s.question;
      if (s.phase !== 'clarifying' || !q || !isValid(q, a.optionIds, a.text)) return s;
      const labels = q.options.filter(o => a.optionIds.includes(o.id)).map(o => o.label);
      const text = a.text?.trim() || undefined;
      const respuesta = [...labels, ...(text ? [text] : [])].join(' · ');
      const ans: Answer = { questionId: q.id, pregunta: q.text, respuesta, optionIds: a.optionIds, ...(text ? { text } : {}) };
      return advance({ ...s, answers: [...s.answers, ans] });
    }
    case 'CONFIRM': return s.phase === 'confirming' ? { ...s, phase: 'registered' } : s;
    case 'CORRECT':
      if (s.phase !== 'confirming' && s.phase !== 'clarifying') return s;
      return advance({ phase: 'clarifying', rating: s.rating, answers: [] });
    default: return s;
  }
}

const lowerFirst = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
function joinY(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}
function labelsOf(s: MachineState, qid: string): string[] {
  const a = s.answers.find(x => x.questionId === qid);
  const q = Q[qid];
  return a ? q.options.filter(o => a.optionIds.includes(o.id)).map(o => o.label) : [];
}
const textOf = (s: MachineState, qid: string) => s.answers.find(x => x.questionId === qid)?.text || '';

export function buildSummary(s: MachineState): string {
  if (s.rating === 'mejorable') {
    const f = optionId(s, 'falta');
    if (f === 'mas_corta') return `Pediré una versión más breve que conserve ${joinY(labelsOf(s, 'conservar').map(lowerFirst))}.`;
    if (f === 'mas_desarrollo') return `Pediré que se desarrolle ${lowerFirst(labelsOf(s, 'desarrollar')[0] || '')}.`;
    return `Pediré que se agregue: «${textOf(s, 'tema')}».`;
  }
  if (s.rating === 'error') {
    const parte = lowerFirst(labelsOf(s, 'parte')[0] || 'la respuesta');
    const que = lowerFirst(labelsOf(s, 'que_mal')[0] || '');
    const t = textOf(s, 'que_mal');
    return `Pediré verificar ${parte}. Señalas: ${que}${t ? ` («${t}»)` : ''}.`;
  }
  if (s.rating === 'duda') return `Pediré aclarar: «${textOf(s, 'duda')}».`;
  return '';
}

export function buildRequest(s: MachineState) {
  const motivo = s.rating === 'error' ? 'error' : s.rating === 'duda' ? 'aclarar' : (optionId(s, 'falta') || 'falta_algo');
  const parrafos = s.rating === 'error' ? labelsOf(s, 'parte') : labelsOf(s, 'conservar');
  const detalle = s.answers.map(a => a.text).filter(Boolean).join(' · ');
  const aclaraciones = s.answers.map(a => ({ pregunta: a.pregunta, respuesta: a.respuesta }));
  return { motivo, parrafos, detalle, aclaraciones };
}
